import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  onSnapshot,
  query,
  orderBy,
} from "firebase/firestore";
import { db } from "../lib/firebase";
import {
  ProposalAddendum,
  AddendumAcknowledgmentStatus,
  MilestonePresets,
  Opportunity,
} from "../types";
import { upsertAutoMilestones } from "./milestonesService";

/**
 * Subscribes to the addenda subcollection of an opportunity.
 */
export function subscribeToAddenda(
  opportunityId: string,
  callback: (addenda: ProposalAddendum[]) => void,
  onError?: (err: Error) => void,
): () => void {
  const colRef = collection(db, "opportunities", opportunityId, "addenda");
  const q = query(colRef, orderBy("issueDate", "desc"));

  return onSnapshot(
    q,
    (snapshot) => {
      const items: ProposalAddendum[] = snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          opportunityId,
          number: String(data.number || ""),
          title: String(data.title || ""),
          issueDate: String(data.issueDate || ""),
          summary: String(data.summary || ""),
          shiftsSubmissionDeadline: Boolean(data.shiftsSubmissionDeadline),
          newSubmissionDate: data.newSubmissionDate ? String(data.newSubmissionDate) : undefined,
          newSubmissionTime: data.newSubmissionTime ? String(data.newSubmissionTime) : undefined,
          shiftsQuestionsDeadline: Boolean(data.shiftsQuestionsDeadline),
          newQuestionsDate: data.newQuestionsDate ? String(data.newQuestionsDate) : undefined,
          acknowledgmentRequired: Boolean(data.acknowledgmentRequired),
          acknowledgmentStatus:
            (data.acknowledgmentStatus as AddendumAcknowledgmentStatus) ||
            (data.acknowledgmentRequired ? "pending_acknowledgment" : "not_required"),
          acknowledgedAt: data.acknowledgedAt ? String(data.acknowledgedAt) : undefined,
          acknowledgedByEmail: data.acknowledgedByEmail
            ? String(data.acknowledgedByEmail)
            : undefined,
          acknowledgedByName: data.acknowledgedByName ? String(data.acknowledgedByName) : undefined,
          affectedRequirementIds: Array.isArray(data.affectedRequirementIds)
            ? data.affectedRequirementIds
            : [],
          affectedFormIds: Array.isArray(data.affectedFormIds) ? data.affectedFormIds : [],
          documentPath: data.documentPath ? String(data.documentPath) : undefined,
          documentName: data.documentName ? String(data.documentName) : undefined,
          createdAt: String(data.createdAt || new Date().toISOString()),
          updatedAt: String(data.updatedAt || new Date().toISOString()),
          createdByEmail: String(data.createdByEmail || ""),
        };
      });
      callback(items);
    },
    (err) => {
      console.error(`Error subscribing to addenda for ${opportunityId}:`, err);
      if (onError) onError(err);
    },
  );
}

export interface CreateAddendumOptions {
  autoShiftOpportunityDates?: boolean;
  currentOpportunity?: Opportunity;
  milestonePresets?: MilestonePresets;
}

/**
 * Creates a new addendum, optionally propagating shifted deadlines to the Opportunity
 * and recalculating internal milestones, and tags impacted requirements.
 */
export async function createAddendum(
  opportunityId: string,
  data: Omit<ProposalAddendum, "id" | "opportunityId" | "createdAt" | "updatedAt">,
  user: { email?: string | null; displayName?: string | null },
  options: CreateAddendumOptions = { autoShiftOpportunityDates: true },
): Promise<string> {
  const now = new Date().toISOString();
  const colRef = collection(db, "opportunities", opportunityId, "addenda");

  const cleanData: Record<string, unknown> = {
    number: data.number.trim(),
    title: data.title.trim(),
    issueDate: data.issueDate,
    summary: data.summary.trim(),
    shiftsSubmissionDeadline: Boolean(data.shiftsSubmissionDeadline),
    shiftsQuestionsDeadline: Boolean(data.shiftsQuestionsDeadline),
    acknowledgmentRequired: Boolean(data.acknowledgmentRequired),
    acknowledgmentStatus: data.acknowledgmentStatus,
    affectedRequirementIds: data.affectedRequirementIds || [],
    affectedFormIds: data.affectedFormIds || [],
    createdByEmail: user.email || data.createdByEmail || "",
    createdAt: now,
    updatedAt: now,
  };

  if (data.newSubmissionDate) cleanData.newSubmissionDate = data.newSubmissionDate;
  if (data.newSubmissionTime) cleanData.newSubmissionTime = data.newSubmissionTime;
  if (data.newQuestionsDate) cleanData.newQuestionsDate = data.newQuestionsDate;
  if (data.acknowledgedAt) cleanData.acknowledgedAt = data.acknowledgedAt;
  if (data.acknowledgedByEmail) cleanData.acknowledgedByEmail = data.acknowledgedByEmail;
  if (data.acknowledgedByName) cleanData.acknowledgedByName = data.acknowledgedByName;
  if (data.documentPath) cleanData.documentPath = data.documentPath;
  if (data.documentName) cleanData.documentName = data.documentName;

  const docRef = await addDoc(colRef, cleanData);

  // Propagate shifted dates to the opportunity if enabled
  if (options.autoShiftOpportunityDates) {
    const oppUpdates: Record<string, unknown> = {};

    if (data.shiftsSubmissionDeadline && data.newSubmissionDate) {
      oppUpdates.dueDate = data.newSubmissionDate;
      if (data.newSubmissionTime) {
        oppUpdates.dueTime = data.newSubmissionTime;
      }
    }

    if (data.shiftsQuestionsDeadline && data.newQuestionsDate) {
      oppUpdates.questionsDueDate = data.newQuestionsDate;
    }

    if (Object.keys(oppUpdates).length > 0) {
      oppUpdates.updatedAt = now;
      await updateDoc(doc(db, "opportunities", opportunityId), oppUpdates);

      // Recalculate milestones with the new submission deadline
      if (data.shiftsSubmissionDeadline && data.newSubmissionDate) {
        try {
          const oppForMilestones: Opportunity = {
            id: opportunityId,
            client: options.currentOpportunity?.client || "",
            title: options.currentOpportunity?.title || "",
            services: options.currentOpportunity?.services || [],
            stage: options.currentOpportunity?.stage || "drafting",
            owner: options.currentOpportunity?.owner || "",
            createdAt: options.currentOpportunity?.createdAt || now,
            ...options.currentOpportunity,
            dueDate: data.newSubmissionDate,
            dueTime: data.newSubmissionTime || options.currentOpportunity?.dueTime,
            updatedAt: now,
          };
          await upsertAutoMilestones(oppForMilestones, options.milestonePresets);
        } catch (err) {
          console.error("Failed to auto-shift milestones following addendum:", err);
        }
      }
    }
  }

  // Tag affected requirements
  if (data.affectedRequirementIds && data.affectedRequirementIds.length > 0) {
    for (const reqId of data.affectedRequirementIds) {
      try {
        const reqRef = doc(db, "opportunities", opportunityId, "requirements", reqId);
        const snap = await getDoc(reqRef);
        if (snap.exists()) {
          const existing = (snap.data().addendumTags as string[]) || [];
          if (!existing.includes(data.number.trim())) {
            await updateDoc(reqRef, {
              addendumTags: [...existing, data.number.trim()],
              updatedAt: now,
            });
          }
        }
      } catch (err) {
        console.error(`Failed to tag requirement ${reqId} with addendum:`, err);
      }
    }
  }

  return docRef.id;
}

/**
 * Updates an addendum and updates requirement tag associations if modified.
 */
export async function updateAddendum(
  opportunityId: string,
  addendumId: string,
  updates: Partial<ProposalAddendum>,
  previousNumber?: string,
  previousAffectedReqIds: string[] = [],
): Promise<void> {
  const docRef = doc(db, "opportunities", opportunityId, "addenda", addendumId);
  const now = new Date().toISOString();

  const cleanUpdates: Record<string, unknown> = {
    ...updates,
    updatedAt: now,
  };

  // Remove undefined properties
  Object.keys(cleanUpdates).forEach((key) => {
    if (cleanUpdates[key] === undefined) {
      delete cleanUpdates[key];
    }
  });

  await updateDoc(docRef, cleanUpdates);

  const newNumber = updates.number ? updates.number.trim() : previousNumber;
  const currentReqIds = updates.affectedRequirementIds ?? previousAffectedReqIds;

  // If number changed or requirement list changed, sync requirement tags
  if (newNumber && (previousNumber !== newNumber || updates.affectedRequirementIds !== undefined)) {
    const toRemove = previousAffectedReqIds.filter((id) => !currentReqIds.includes(id));
    const toAdd = currentReqIds.filter((id) => !previousAffectedReqIds.includes(id));

    // Remove old tag from removed requirements
    for (const reqId of toRemove) {
      try {
        const reqRef = doc(db, "opportunities", opportunityId, "requirements", reqId);
        const snap = await getDoc(reqRef);
        if (snap.exists()) {
          const tags = (snap.data().addendumTags as string[]) || [];
          const updatedTags = tags.filter((t) => t !== previousNumber && t !== newNumber);
          await updateDoc(reqRef, { addendumTags: updatedTags, updatedAt: now });
        }
      } catch (err) {
        console.error(`Failed to untag requirement ${reqId}:`, err);
      }
    }

    // Add tag to added requirements
    for (const reqId of toAdd) {
      try {
        const reqRef = doc(db, "opportunities", opportunityId, "requirements", reqId);
        const snap = await getDoc(reqRef);
        if (snap.exists()) {
          const tags = (snap.data().addendumTags as string[]) || [];
          if (!tags.includes(newNumber)) {
            await updateDoc(reqRef, { addendumTags: [...tags, newNumber], updatedAt: now });
          }
        }
      } catch (err) {
        console.error(`Failed to tag requirement ${reqId}:`, err);
      }
    }
  }
}

/**
 * Deletes an addendum and strips its tag from any associated requirements.
 */
export async function deleteAddendum(
  opportunityId: string,
  addendumId: string,
  addendumNumber?: string,
  affectedReqIds: string[] = [],
): Promise<void> {
  const docRef = doc(db, "opportunities", opportunityId, "addenda", addendumId);
  await deleteDoc(docRef);

  if (addendumNumber && affectedReqIds.length > 0) {
    const now = new Date().toISOString();
    for (const reqId of affectedReqIds) {
      try {
        const reqRef = doc(db, "opportunities", opportunityId, "requirements", reqId);
        const snap = await getDoc(reqRef);
        if (snap.exists()) {
          const tags = (snap.data().addendumTags as string[]) || [];
          const updatedTags = tags.filter((t) => t !== addendumNumber);
          await updateDoc(reqRef, { addendumTags: updatedTags, updatedAt: now });
        }
      } catch (err) {
        console.error(`Failed to remove addendum tag from requirement ${reqId}:`, err);
      }
    }
  }
}

/**
 * Toggles acknowledgment status for an addendum.
 */
export async function toggleAddendumAcknowledgment(
  opportunityId: string,
  addendumId: string,
  isAcknowledged: boolean,
  user: { email?: string | null; displayName?: string | null },
): Promise<void> {
  const docRef = doc(db, "opportunities", opportunityId, "addenda", addendumId);
  const now = new Date().toISOString();

  if (isAcknowledged) {
    await updateDoc(docRef, {
      acknowledgmentStatus: "acknowledged",
      acknowledgedAt: now,
      acknowledgedByEmail: user.email || "user@mlconnections.com",
      acknowledgedByName: user.displayName || user.email || "Staff Member",
      updatedAt: now,
    });
  } else {
    await updateDoc(docRef, {
      acknowledgmentStatus: "pending_acknowledgment",
      acknowledgedAt: null,
      acknowledgedByEmail: null,
      acknowledgedByName: null,
      updatedAt: now,
    });
  }
}
